// ─── DDQuestionnaireClient.jsx ────────────────────────────────────────────────
// Public-facing Due Diligence Questionnaire
// Shared via secure token: https://app.com?ddq=TOKEN
//
// Features:
//  • Comprehensive self-assessment questionnaire
//  • No authentication required (token-based access)
//  • File uploads for supporting documents
//  • Digital certification/signature
//  • Progress tracking
//  • Auto-save functionality
// ────────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./theme";
import { db } from "./dbClient";
import { DD_QUESTIONNAIRE_SECTIONS, getQuestionsForSection } from "./DDQuestionnaireTemplate";

const C = {
  bg: "#0D1117",
  surface: "#161B22",
  border: "#30363D",
  text: "#C9D1D9",
  textMid: "#8B949E",
  textDim: "#6E7681",
  accent: "#58A6FF",
  success: "#3FB950",
  danger: "#F85149",
  warn: "#F0883E",
};

export default function DDQuestionnaireClient({ tokenParam }) {
  const { t } = useTranslation();
  const [token, setToken] = useState(tokenParam);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [entityInfo, setEntityInfo] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [autoSaveInterval, setAutoSaveInterval] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Load questionnaire by token
  useEffect(() => {
    async function loadQuestionnaire() {
      if (!token) {
        setError("No questionnaire token provided");
        setLoading(false);
        return;
      }

      try {
        const { data, error: dbError } = await db
          .from("dd_questionnaire_links")
          .select("*")
          .eq("token", token)
          .single()
          .execute();

        if (dbError || !data) {
          setError("Questionnaire link not found or has expired");
          setLoading(false);
          return;
        }

        if (data.expires_at && new Date(data.expires_at) < new Date()) {
          setError("This questionnaire link has expired");
          setLoading(false);
          return;
        }

        if (data.completed_at) {
          setError("This questionnaire has already been submitted");
          setLoading(false);
          return;
        }

        setEntityInfo({
          entityName: data.entity_name,
          thirdPartyDDId: data.third_party_dd_id,
          companyId: data.company_id,
        });

        // Try to load existing draft
        if (data.third_party_dd_id) {
          const { data: existing } = await db
            .from("third_party_dd_questionnaires")
            .select("*")
            .eq("third_party_dd_id", data.third_party_dd_id)
            .eq("status", "draft")
            .order("created_at", false)
            .limit(1)
            .execute();

          if (existing && existing.length > 0) {
            setAnswers(existing[0].questionnaire_data || {});
            setQuestionnaire(existing[0]);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading questionnaire:", err);
        setError("Failed to load questionnaire");
        setLoading(false);
      }
    }

    loadQuestionnaire();
  }, [token]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (!questionnaire?.id) return;

    const interval = setInterval(async () => {
      try {
        await db
          .from("third_party_dd_questionnaires")
          .update({ questionnaire_data: answers })
          .eq("id", questionnaire.id)
          .execute();
      } catch (err) {
        console.error("Auto-save failed:", err);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [questionnaire?.id, answers]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(p => ({ ...p, [questionId]: value }));
  };

  const handleFileUpload = async (questionId, files) => {
    if (!files?.length) return;

    const file = files[0];
    // In a real implementation, upload to storage (Supabase, S3, etc.)
    // For now, just store file metadata
    setUploadedFiles(p => ({
      ...p,
      [questionId]: {
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
      },
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      // Create submission record
      const { data: submitted, error: submitError } = await db
        .from("third_party_dd_questionnaires")
        .update({
          questionnaire_data: answers,
          status: "submitted",
        })
        .eq("id", questionnaire.id)
        .execute();

      if (submitError) throw submitError;

      // Mark link as completed
      await db
        .from("dd_questionnaire_links")
        .update({ completed_at: new Date().toISOString() })
        .eq("token", token)
        .execute();

      setError(null);
      setAnswers({});
      alert("Thank you! Your questionnaire has been submitted successfully.");
      setCurrentSection(0);
    } catch (err) {
      console.error("Error submitting questionnaire:", err);
      setError("Failed to submit questionnaire. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: C.bg,
          color: C.text,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <div>Loading questionnaire…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: C.bg,
          color: C.text,
        }}
      >
        <div
          style={{
            background: C.surface,
            border: `1px solid ${C.danger}`,
            borderRadius: 10,
            padding: 24,
            maxWidth: 500,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ color: C.danger, margin: 0, marginBottom: 12 }}>
            Access Denied
          </h2>
          <p style={{ color: C.textMid, margin: 0 }}>{error}</p>
        </div>
      </div>
    );
  }

  const section = DD_QUESTIONNAIRE_SECTIONS[currentSection];
  const questions = getQuestionsForSection(section.id).filter(
    q => !q.internal
  );
  const completionPercent = Math.round(
    (Object.keys(answers).length / Object.keys(DD_QUESTIONNAIRE_SECTIONS.flatMap(s => getQuestionsForSection(s.id).filter(q => !q.internal))).length) * 100
  );

  const S = {
    card: {
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: 20,
    },
    input: {
      background: C.bg,
      border: `1px solid ${C.border}`,
      borderRadius: 6,
      color: C.text,
      padding: "8px 12px",
      fontSize: 13,
      fontFamily: "'DM Mono', monospace",
      width: "100%",
      boxSizing: "border-box",
    },
    label: {
      color: C.textMid,
      fontSize: 11,
      display: "block",
      marginBottom: 6,
      fontWeight: 600,
      letterSpacing: "0.05em",
    },
    btn: (primary = false) => ({
      background: primary ? C.accent : "transparent",
      border: `1px solid ${primary ? C.accent : C.border}`,
      borderRadius: 6,
      color: primary ? C.bg : C.text,
      padding: "8px 16px",
      cursor: "pointer",
      fontSize: 12,
      fontFamily: "'DM Mono', monospace",
      fontWeight: 600,
    }),
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, padding: 20 }}>
      {/* Header */}
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
          marginBottom: 30,
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: 28, margin: 0, marginBottom: 8 }}>
          {t('thirdPartyDD.questionnaire.title')}
        </h1>
        <p style={{ color: C.textMid, margin: 0, marginBottom: 4 }}>
          {entityInfo?.entityName}
        </p>
        <p style={{ color: C.textDim, margin: 0, fontSize: 12 }}>
          {t('thirdPartyDD.questionnaire.messages.autoFillNotice')}
        </p>
      </div>

      {/* Progress Bar */}
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 8,
            fontSize: 11,
            color: C.textMid,
          }}
        >
          <span>
            {t('common.section', { defaultValue: 'Section' })} {currentSection + 1} of {DD_QUESTIONNAIRE_SECTIONS.length}
          </span>
          <span>{completionPercent}% {t('common.complete', { defaultValue: 'Complete' })}</span>
        </div>
        <div
          style={{
            background: C.border,
            borderRadius: 4,
            height: 6,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: C.accent,
              height: "100%",
              width: `${(completionPercent || 0) * 0.01 * 100}%`,
              transition: "width 0.3s",
            }}
          />
        </div>
      </div>

      {/* Main Content */}
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 2.5fr",
          gap: 20,
        }}
      >
        {/* Sidebar Navigation */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {DD_QUESTIONNAIRE_SECTIONS.map((sec, idx) => (
            <button
              key={sec.id}
              onClick={() => setCurrentSection(idx)}
              style={{
                ...S.btn(idx === currentSection),
                textAlign: "left",
                padding: "12px 14px",
                fontSize: 12,
              }}
            >
              <span style={{ marginRight: 6 }}>{sec.icon}</span>
              <span>{sec.title}</span>
              {idx < currentSection && (
                <span style={{ marginLeft: "auto", color: C.success }}>✓</span>
              )}
            </button>
          ))}
        </div>

        {/* Questionnaire Content */}
        <div style={S.card}>
          <h2 style={{ color: C.text, fontSize: 18, margin: 0, marginBottom: 8 }}>
            {section.icon} {section.title}
          </h2>
          <p style={{ color: C.textMid, fontSize: 12, margin: 0, marginBottom: 20 }}>
            {section.description}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {questions.map(q => {
              // Check conditional visibility
              if (q.conditional) {
                const conditionalAnswers = Array.isArray(q.conditionalValue)
                  ? q.conditionalValue
                  : [q.conditionalValue];
                if (!conditionalAnswers.includes(answers[q.conditional])) {
                  return null;
                }
              }

              return (
                <div key={q.id}>
                  <label style={S.label}>
                    {q.question}
                    {q.required && (
                      <span style={{ color: C.danger, marginLeft: 4 }}>*</span>
                    )}
                  </label>
                  {q.hint && (
                    <div style={{ color: C.textDim, fontSize: 11, marginBottom: 6 }}>
                      💡 {q.hint}
                    </div>
                  )}

                  {q.type === "text" && (
                    <input
                      type="text"
                      value={answers[q.id] || ""}
                      onChange={e => handleAnswerChange(q.id, e.target.value)}
                      style={S.input}
                    />
                  )}

                  {q.type === "email" && (
                    <input
                      type="email"
                      value={answers[q.id] || ""}
                      onChange={e => handleAnswerChange(q.id, e.target.value)}
                      style={S.input}
                    />
                  )}

                  {q.type === "date" && (
                    <input
                      type="date"
                      value={answers[q.id] || ""}
                      onChange={e => handleAnswerChange(q.id, e.target.value)}
                      style={S.input}
                    />
                  )}

                  {q.type === "textarea" && (
                    <textarea
                      value={answers[q.id] || ""}
                      onChange={e => handleAnswerChange(q.id, e.target.value)}
                      rows={3}
                      style={{ ...S.input, resize: "vertical" }}
                    />
                  )}

                  {q.type === "select" && (
                    <select
                      value={answers[q.id] || ""}
                      onChange={e => handleAnswerChange(q.id, e.target.value)}
                      style={S.input}
                    >
                      <option value="">Select…</option>
                      {q.options.map(opt => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}

                  {q.type === "checkbox" && (
                    <button
                      onClick={() => handleAnswerChange(q.id, !answers[q.id])}
                      style={{
                        ...S.btn(answers[q.id]),
                        textAlign: "left",
                        justifyContent: "flex-start",
                      }}
                    >
                      <span style={{ marginRight: 8 }}>
                        {answers[q.id] ? "☑" : "☐"}
                      </span>
                      {q.label || q.question}
                    </button>
                  )}

                  {q.type === "file" && (
                    <div>
                      <input
                        type="file"
                        onChange={e => handleFileUpload(q.id, e.target.files)}
                        style={{
                          ...S.input,
                          padding: "12px",
                          cursor: "pointer",
                        }}
                      />
                      {uploadedFiles[q.id] && (
                        <div
                          style={{
                            marginTop: 8,
                            padding: "8px 10px",
                            background: `${C.success}22`,
                            borderRadius: 4,
                            fontSize: 11,
                            color: C.success,
                          }}
                        >
                          ✓ {uploadedFiles[q.id].name}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Navigation */}
          <div
            style={{
              display: "flex",
              gap: 12,
              marginTop: 20,
              paddingTop: 20,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              style={{
                ...S.btn(false),
                opacity: currentSection === 0 ? 0.5 : 1,
                flex: 1,
              }}
            >
              ← {t('common.previous')}
            </button>

            {currentSection === DD_QUESTIONNAIRE_SECTIONS.length - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ ...S.btn(true), flex: 1 }}
              >
                {submitting ? t('thirdPartyDD.questionnaire.submitting') : t('thirdPartyDD.questionnaire.submitQuestionnaire')}
              </button>
            ) : (
              <button
                onClick={() =>
                  setCurrentSection(
                    Math.min(
                      DD_QUESTIONNAIRE_SECTIONS.length - 1,
                      currentSection + 1
                    )
                  )
                }
                style={{ ...S.btn(true), flex: 1 }}
              >
                {t('common.next')} →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          maxWidth: 900,
          margin: "40px auto 0",
          paddingTop: 20,
          borderTop: `1px solid ${C.border}`,
          textAlign: "center",
          color: C.textDim,
          fontSize: 11,
        }}
      >
        <p style={{ margin: 0 }}>
          {t('common.autoSaveNotice', { defaultValue: 'This questionnaire will be automatically saved as you complete it.' })}
        </p>
        <p style={{ margin: 0, marginTop: 4 }}>
          {t('common.securityNotice', { defaultValue: 'All information is encrypted and securely transmitted.' })}
        </p>
      </div>
    </div>
  );
}
